import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Colors } from '../theme/tokens';
import { useTheme } from '../hooks/useTheme';

const GRADIENTS = Colors.wallpapers;

// Realistic fine 35mm film grain SVG filter
const REALISTIC_FILM_GRAIN = `url("data:image/svg+xml,%3Csvg width='300' height='300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='filmGrain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23filmGrain)'/%3E%3C/svg%3E")`;

interface RawIcon {
  id: string;
  label: string;
  path: string;
  size: number;
}

// Full Set: Transfer Actions (Download, Upload, Share, Link, Drive, Zap, Globe, Lock, Shield) + File Types + Torrents
const CANVAS_ICONS: RawIcon[] = [
  // Transfer & P2P Actions
  { id: 'download', label: 'P2P Download', size: 42, path: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3' },
  { id: 'upload', label: 'P2P Upload', size: 42, path: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12' },
  { id: 'share', label: 'WebRTC Mesh Share', size: 40, path: 'M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M8.59 13.51l6.83 3.98 M15.41 6.51l-6.82 3.98' },
  { id: 'link', label: 'Magnet Short Link', size: 38, path: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' },
  { id: 'drive', label: 'Local Storage Drive', size: 40, path: 'M22 12H2 M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z M6 16h.01' },
  { id: 'zap', label: 'Lightning Speed P2P', size: 44, path: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z' },
  { id: 'globe', label: 'Global Swarm Network', size: 46, path: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' },
  { id: 'lock', label: 'E2E Encrypted Lock', size: 36, path: 'M3 13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7z M7 11V7a5 5 0 0 1 10 0v4' },
  { id: 'shield', label: 'DTLS Shield', size: 38, path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },

  // Torrent & P2P Icons
  { id: 'magnet', label: 'Torrent Magnet', size: 44, path: 'M6 15v-3a6 6 0 1 1 12 0v3m-12 0H3v-3a9 9 0 1 1 18 0v3h-3m-12 0h6m6 0h-6' },
  { id: 'torrent', label: '.torrent File', size: 42, path: 'M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5' },
  { id: 'peer', label: 'P2P Peer Node', size: 40, path: 'M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M8.59 13.51l6.83 3.98 M15.41 6.51l-6.82 3.98' },
  { id: 'seed', label: 'Torrent Seeder', size: 42, path: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12' },

  // File Format Icons
  { id: 'zip', label: 'ZIP Archive', size: 42, path: 'M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6z M12 3v18 M9 7h6 M9 11h6 M9 15h6' },
  { id: 'pdf', label: 'PDF Document', size: 38, path: 'M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z M14 2v6h6 M10 12h4 M10 16h4' },
  { id: 'doc', label: 'Text Document', size: 38, path: 'M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z M14 2v6h6 M16 13H8 M16 17H8' },
  { id: 'image', label: 'Photo Image', size: 38, path: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21' },
  { id: 'video', label: 'Video Movie', size: 40, path: 'M2 4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4z M7 2v20 M17 2v20 M2 12h20' },
  { id: 'audio', label: 'Audio Music Track', size: 38, path: 'M9 18V5l12-2v13 M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
  { id: 'code', label: 'Source Code File', size: 36, path: 'M16 18l6-6-6-6 M8 6l-6 6 6 6' },
  { id: 'folder', label: 'Folder Directory', size: 40, path: 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z' },
  { id: 'executable', label: 'Executable App Binary', size: 38, path: 'M4 4h16v16H4z M8 8l4 4-4 4 M14 16h4' },
  { id: 'sheet', label: 'Spreadsheet File', size: 38, path: 'M3 3h18v18H3z M3 9h18 M3 15h18 M9 3v18 M15 3v18' },
  { id: 'database', label: 'Database File', size: 36, path: 'M12 2c4.97 0 9 1.34 9 3v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5c0-1.66 4.03-3 9-3z M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3 M3 5v14 M21 5v14' },
  { id: 'archive', label: '7z Archive', size: 40, path: 'M4 4h16v16H4z M12 4v16 M8 8h8 M8 12h8 M8 16h8' },
  { id: 'iso', label: 'Disk ISO Image', size: 40, path: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
  { id: 'cpu', label: 'Piece SHA-1 Verification', size: 38, path: 'M4 4h16v16H4z M9 9h6v6H9z M9 1v3 M15 1v3 M9 20v3 M15 20v3 M20 9h3 M20 15h3 M1 9h3 M1 15h3' },
  { id: 'activity', label: 'Live Transfer Rate', size: 38, path: 'M22 12h-4l-4 10L10 2 6 12H2' },
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  size: number;
  angle: number;
  vRot: number;
  label: string;
  path2d: Path2D | null;
}

export const DynamicBackground = () => {
  const { colors, isDark } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const themeWallpapers = Colors.wallpapers;

  const opacities = useRef([0, 1, 2, 3].map((_, i) => new Animated.Value(i === 0 ? 1 : 0))).current;

  // Wallpaper color shift timer
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % 4;

      Animated.timing(opacities[nextIndex], {
        toValue: 1,
        duration: 3500,
        useNativeDriver: false,
      }).start();

      Animated.timing(opacities[currentIndex], {
        toValue: 0,
        duration: 3500,
        useNativeDriver: false,
      }).start(() => {
        setCurrentIndex(nextIndex);
      });
    }, 20000);

    return () => clearInterval(interval);
  }, [currentIndex, opacities]);

  // HTML5 Canvas Colliding Physics Simulation (Pure Autonomous Floating)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    // Initialize physics particles spread across viewport
    const particles: Particle[] = CANVAS_ICONS.map((icon, idx) => {
      const radius = icon.size / 2 + 4;
      const cols = 5;
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      const x = (col + 0.5) * (width / cols) + (Math.random() * 50 - 25);
      const y = (row + 0.5) * (height / 6) + (Math.random() * 50 - 25);

      // Steady low-gravity speed (1.0 - 2.0 px/frame)
      const speed = 1.0 + Math.random() * 1.0;
      const angle = Math.random() * Math.PI * 2;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      let path2d: Path2D | null = null;
      try {
        path2d = new Path2D(icon.path);
      } catch (e) {
        path2d = null;
      }

      return {
        x: Math.max(radius, Math.min(width - radius, x)),
        y: Math.max(radius, Math.min(height - radius, y)),
        vx,
        vy,
        radius,
        size: icon.size,
        angle: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.008,
        label: icon.label,
        path2d,
      };
    });

    // Physics Update & Render Loop
    const loop = () => {
      ctx.clearRect(0, 0, width, height);

      // 1. Move particles & boundary bounce
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.angle += p.vRot;

        // Boundary Bounce (Window Edges)
        if (p.x - p.radius < 0) {
          p.x = p.radius;
          p.vx = Math.abs(p.vx);
        } else if (p.x + p.radius > width) {
          p.x = width - p.radius;
          p.vx = -Math.abs(p.vx);
        }

        if (p.y - p.radius < 0) {
          p.y = p.radius;
          p.vy = Math.abs(p.vy);
        } else if (p.y + p.radius > height) {
          p.y = height - p.radius;
          p.vy = -Math.abs(p.vy);
        }
      }

      // 2. Elastic Collision between Floating Icons ("if they touch move back")
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];

          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const dist = Math.hypot(dx, dy);
          const minDist = p1.radius + p2.radius + 8;

          // Collision detected!
          if (dist < minDist && dist > 0) {
            const nx = dx / dist;
            const ny = dy / dist;

            const overlap = (minDist - dist) / 2;
            p1.x -= nx * overlap;
            p1.y -= ny * overlap;
            p2.x += nx * overlap;
            p2.y += ny * overlap;

            const kx = p1.vx - p2.vx;
            const ky = p1.vy - p2.vy;
            const p = 2 * (nx * kx + ny * ky) / 2;

            p1.vx -= p * nx;
            p1.vy -= p * ny;
            p2.vx += p * nx;
            p2.vy += p * ny;
          }
        }
      }

      // 3. Draw Constellation Network Lines between close icons (< 220px)
      const MAX_LINK_DIST = 220;
      const rgb = '255, 255, 255';
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

          if (dist < MAX_LINK_DIST) {
            const alpha = (1 - dist / MAX_LINK_DIST) * 0.14;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // 4. Draw Floating SVG Icons (Low visibility, non-distracting subtle stroke)
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (!p.path2d) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);

        const scale = p.size / 24;
        ctx.scale(scale, scale);
        ctx.translate(-12, -12);

        ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetY = 2;

        ctx.strokeStyle = `rgba(${rgb}, 0.22)`;
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke(p.path2d);

        ctx.restore();
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <View style={styles.container}>
      {/* 1. Dynamic Wallpaper Gradients */}
      {[0, 1, 2, 3].map((_, i) => (
        <Animated.View
          key={i}
          style={[
            StyleSheet.absoluteFillObject,
            {
              opacity: opacities[i],
              backgroundColor: themeWallpapers[i],
            } as any,
          ]}
        />
      ))}

      {/* 2. Realistic Fine Film Grain Overlay */}
      <View
        style={[
          StyleSheet.absoluteFillObject,
          {
            backgroundImage: REALISTIC_FILM_GRAIN,
            backgroundRepeat: 'repeat',
            opacity: 0.35,
            mixBlendMode: 'overlay',
            pointerEvents: 'none',
          } as any,
        ]}
      />

      {/* 3. HTML5 Colliding Physics Canvas */}
      {typeof window !== 'undefined' && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: -1,
    overflow: 'hidden',
  },
});
